import { createFileRoute } from "@tanstack/react-router";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Plus, Mail, Phone, Trash2 } from "lucide-react";
import {
  listContacts,
  saveContact,
  deleteContact,
  listInteractions,
  addInteraction,
} from "@/lib/momentum.functions";
import { useBusiness } from "@/components/business-context";
import { formatDay, todayISO } from "@/lib/momentum-types";
import type { Contact } from "@/lib/momentum-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useOfflineFn } from "@/lib/offline";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/app/contacts")({
  head: () => ({
    meta: [
      { title: "Contacts — The Founders App" },
      {
        name: "description",
        content: "A phone book per business, with a running log of every interaction.",
      },
      { property: "og:title", content: "Contacts — The Founders App" },
      { property: "og:description", content: "Track outreach history person by person." },
    ],
  }),
  component: ContactsPage,
});

interface Draft {
  id?: string;
  name: string;
  phone: string;
  email: string;
  notes: string;
}

const EMPTY: Draft = { name: "", phone: "", email: "", notes: "" };

function ContactsPage() {
  const { active } = useBusiness();
  const businessId = active?.id ?? "";
  const [draft, setDraft] = useState<Draft | null>(null);
  const [openContact, setOpenContact] = useState<Contact | null>(null);

  const fetchContacts = useServerFn(listContacts);
  const persist = useOfflineFn("saveContact");
  const remove = useOfflineFn("deleteContact");

  const query = useQuery({
    queryKey: ["contacts", businessId],
    enabled: Boolean(businessId),
    queryFn: () => fetchContacts({ data: { businessId } }),
  });

  const save = async () => {
    if (!draft?.name.trim()) return;
    try {
      await persist({ data: { ...draft, businessId } });
      setDraft(null);
      void query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    }
  };

  if (!businessId) return null;
  const contacts = (query.data ?? []) as Contact[];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="micro-label">Ledger</p>
          <h2 className="mt-2 text-xl font-medium">Phone book</h2>
        </div>
        <Button size="sm" onClick={() => setDraft(EMPTY)}>
          <Plus className="mr-2 h-4 w-4" /> Add contact
        </Button>
      </div>

      {contacts.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No one here yet. Add the first person you're in conversation with.
        </p>
      )}

      <ul className="space-y-3">
        {contacts.map((c) => (
          <li key={c.id} className="soft-panel p-5">
            <div className="flex items-start justify-between gap-4">
              <button className="text-left" onClick={() => setOpenContact(c)}>
                <p className="notranslate font-medium">{c.name}</p>
                <div className="mt-1 flex flex-wrap gap-4 text-xs text-muted-foreground">
                  {c.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3" /> <span className="notranslate">{c.phone}</span>
                    </span>
                  )}
                  {c.email && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-3 w-3" /> <span className="notranslate">{c.email}</span>
                    </span>
                  )}
                </div>
                {c.notes && <p className="notranslate mt-2 text-sm text-muted-foreground">{c.notes}</p>}
              </button>
              <div className="flex shrink-0 gap-2">
                <Button variant="ghost" size="sm" onClick={() => setOpenContact(c)}>
                  Log
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setDraft({
                      id: c.id,
                      name: c.name,
                      phone: c.phone ?? "",
                      email: c.email ?? "",
                      notes: c.notes ?? "",
                    })
                  }
                >
                  Edit
                </Button>
                <ConfirmDelete
                  className="notranslate"
                  ariaLabel={`Delete ${c.name}`}
                  itemLabel={c.name}
                  privateLabel
                  onConfirm={async () => {
                    await remove({ data: { id: c.id } });
                    void query.refetch();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </ConfirmDelete>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={Boolean(draft)} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit contact" : "New contact"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {(["name", "phone", "email"] as const).map((field) => (
              <div key={field} className="space-y-1.5">
                <Label htmlFor={field} className="capitalize">
                  {field}
                </Label>
                <Input
                  id={field}
                  value={draft?.[field] ?? ""}
                  onChange={(e) => setDraft((d) => (d ? { ...d, [field]: e.target.value } : d))}
                />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                className="resize-none"
                value={draft?.notes ?? ""}
                onChange={(e) => setDraft((d) => (d ? { ...d, notes: e.target.value } : d))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => void save()}>Save contact</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InteractionsDialog
        contact={openContact}
        businessId={businessId}
        onClose={() => setOpenContact(null)}
      />
    </div>
  );
}

function InteractionsDialog({
  contact,
  businessId,
  onClose,
}: {
  contact: Contact | null;
  businessId: string;
  onClose: () => void;
}) {
  const [note, setNote] = useState("");
  const [when, setWhen] = useState(todayISO());
  const fetchInteractions = useServerFn(listInteractions);
  const add = useOfflineFn("addInteraction");

  const query = useQuery({
    queryKey: ["interactions", contact?.id],
    enabled: Boolean(contact?.id),
    queryFn: () => fetchInteractions({ data: { contactId: contact!.id } }),
  });

  const submit = async () => {
    if (!contact || !note.trim()) return;
    try {
      await add({ data: { businessId, contactId: contact.id, occurred_on: when, note } });
      setNote("");
      void query.refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save");
    }
  };

  return (
    <Dialog open={Boolean(contact)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="notranslate">{contact?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="date" value={when} onChange={(e) => setWhen(e.target.value)} />
          <Textarea
            className="resize-none"
            placeholder="Called and discussed…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Button onClick={() => void submit()}>Add to log</Button>
        </div>
        <ul className="mt-4 space-y-3 border-t border-border pt-4">
          {(query.data ?? []).map((i) => (
            <li key={i.id}>
              <p className="text-xs text-muted-foreground">{formatDay(i.occurred_on)}</p>
              <p className="notranslate mt-1 text-sm leading-relaxed whitespace-pre-wrap">{i.note}</p>
            </li>
          ))}
          {query.data?.length === 0 && (
            <li className="text-sm text-muted-foreground">No conversations logged yet. Add one line after the next call.</li>
          )}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
